import { useState, useEffect } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { ThumbsUp, ThumbsDown } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { supabase } from "@/integrations/supabase/client";

const FeedbackBar = () => {
  const [liked, setLiked] = useState<boolean | null>(null);
  const [showDislikeDialog, setShowDislikeDialog] = useState(false);
  const [dislikeReason, setDislikeReason] = useState("");
  const [additionalComments, setAdditionalComments] = useState("");
  const [likeCount, setLikeCount] = useState(0);
  const [dislikeCount, setDislikeCount] = useState(0);

  useEffect(() => {
    fetchFeedbackCounts();
  }, []);

  const fetchFeedbackCounts = async () => {
    try {
      const { count: likesCount } = await supabase
        .from("user_feedback")
        .select("*", { count: "exact", head: true })
        .eq("feedback_type", "like");

      const { count: dislikesCount } = await supabase
        .from("user_feedback")
        .select("*", { count: "exact", head: true })
        .eq("feedback_type", "dislike");

      setLikeCount(likesCount || 0);
      setDislikeCount(dislikesCount || 0);
    } catch (error) {
      console.error("Error fetching feedback counts:", error);
    }
  };

  const handleLike = async () => {
    try {
      // Store locally
      const feedback = {
        type: "like",
        timestamp: new Date().toISOString(),
      };
      const existingFeedback = JSON.parse(localStorage.getItem("walkingCalcFeedback") || "[]");
      existingFeedback.push(feedback);
      localStorage.setItem("walkingCalcFeedback", JSON.stringify(existingFeedback));

      // Store in database
      await supabase.from("user_feedback").insert({
        feedback_type: "like",
        user_agent: navigator.userAgent,
      });

      setLiked(true);
      setLikeCount(prev => prev + 1);
      
      toast.success("Thank you for your feedback!", {
        description: "We're glad you found this tool helpful.",
      });
    } catch (error) {
      console.error("Error submitting like:", error);
      setLiked(true);
      toast.success("Thank you for your feedback!");
    }
  };

  const handleDislike = () => {
    setShowDislikeDialog(true);
  };

  const submitDislikeFeedback = async () => {
    if (!dislikeReason) {
      toast.error("Please select a reason");
      return;
    }

    try {
      // Store feedback locally
      const feedback = {
        type: "dislike",
        reason: dislikeReason,
        comments: additionalComments,
        timestamp: new Date().toISOString(),
      };
      
      const existingFeedback = JSON.parse(localStorage.getItem("walkingCalcFeedback") || "[]");
      existingFeedback.push(feedback);
      localStorage.setItem("walkingCalcFeedback", JSON.stringify(existingFeedback));

      // Store in database
      await supabase.from("user_feedback").insert({
        feedback_type: "dislike",
        dislike_reason: dislikeReason,
        comment: additionalComments || null,
        user_agent: navigator.userAgent,
      });

      setLiked(false);
      setShowDislikeDialog(false);
      setDislikeReason("");
      setAdditionalComments("");
      setDislikeCount(prev => prev + 1);

      toast.success("Feedback submitted", {
        description: "Thank you for helping us improve!",
      });
    } catch (error) {
      console.error("Error submitting dislike:", error);
      setLiked(false);
      setShowDislikeDialog(false);
      toast.success("Feedback submitted");
    }
  };

  // Hide feedback bar after user has provided feedback
  if (liked !== null) {
    return null;
  }

  return (
    <>
      <div className="fixed bottom-6 right-6 z-50">
        <Card className="p-4 border-card-border rounded-2xl shadow-medium bg-card">
          <div className="flex items-center gap-3">
            <span className="text-sm font-medium text-foreground">Was this helpful?</span>
            <div className="flex gap-2">
              <Button
                size="sm"
                variant="outline"
                onClick={handleLike}
                className="gap-1"
              >
                <ThumbsUp className="w-4 h-4" />
                {likeCount > 0 && <span className="text-xs">({likeCount})</span>}
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={handleDislike}
                className="gap-1"
              >
                <ThumbsDown className="w-4 h-4" />
                {dislikeCount > 0 && <span className="text-xs">({dislikeCount})</span>}
              </Button>
            </div>
          </div>
        </Card>
      </div>

      <Dialog open={showDislikeDialog} onOpenChange={setShowDislikeDialog}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Help us improve</DialogTitle>
            <DialogDescription>
              Please let us know what went wrong so we can make this tool better.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-3">
              <Label>What was the issue?</Label>
              <RadioGroup value={dislikeReason} onValueChange={setDislikeReason}>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="inaccurate" id="inaccurate" />
                  <Label htmlFor="inaccurate" className="font-normal cursor-pointer">
                    Results seem inaccurate
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="confusing_ui" id="confusing_ui" />
                  <Label htmlFor="confusing_ui" className="font-normal cursor-pointer">
                    Confusing interface
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="bug" id="bug" />
                  <Label htmlFor="bug" className="font-normal cursor-pointer">
                    Something's broken
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="privacy_concern" id="privacy_concern" />
                  <Label htmlFor="privacy_concern" className="font-normal cursor-pointer">
                    Privacy concerns
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="other" id="other" />
                  <Label htmlFor="other" className="font-normal cursor-pointer">
                    Other
                  </Label>
                </div>
              </RadioGroup>
            </div>

            <div className="space-y-2">
              <Label htmlFor="comments">Additional comments (optional)</Label>
              <Textarea
                id="comments"
                placeholder="Tell us more about your experience..."
                value={additionalComments}
                onChange={(e) => setAdditionalComments(e.target.value)}
                rows={4}
              />
            </div>
          </div>

          <div className="flex gap-2 justify-end">
            <Button variant="outline" onClick={() => setShowDislikeDialog(false)}>
              Cancel
            </Button>
            <Button onClick={submitDislikeFeedback} className="bg-primary">
              Submit Feedback
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default FeedbackBar;